/**
 * Audio Metadata Adapter
 * 
 * Handles ID3v2 tags for MP3/FLAC/OGG and MP4 ilst atoms for M4A/MOV
 * with proper cover art mapping.
 */

import { CanonicalMetadata, MetadataAdapter, MetadataProvenance } from '../canonical-schema';

export class AudioAdapter implements MetadataAdapter {
  private musicMetadata: any;

  constructor() {
    // Only initialize on server side
    if (typeof window === 'undefined') {
      this.initializeMusicMetadata();
    }
  }

  private async initializeMusicMetadata() {
    try {
      // Only import on server side
      if (typeof window === 'undefined') {
        const { parseFile } = await import('music-metadata');
        this.musicMetadata = { parseFile };
      }
    } catch (error) {
      console.warn('music-metadata not available, falling back to basic extraction');
    }
  }

  async read(file: File): Promise<CanonicalMetadata> {
    const metadata: CanonicalMetadata = {};

    if (this.musicMetadata) {
      return this.readWithMusicMetadata(file);
    } else {
      return this.readBasic(file);
    }
  }

  private async readWithMusicMetadata(file: File): Promise<CanonicalMetadata> {
    // music-metadata only works on server side, fall back to basic extraction in browser
    if (typeof window !== 'undefined') {
      return this.readBasic(file);
    }

    try {
      const buffer = await file.arrayBuffer();
      const uint8Array = new Uint8Array(buffer);
      
      const parsed = await this.musicMetadata.parseFile(uint8Array);
      const metadata: CanonicalMetadata = {};
      const provenance: MetadataProvenance[] = [];

      // Basic metadata
      if (parsed.common.title) {
        metadata.title = parsed.common.title;
        provenance.push({
          source: this.getTagSource(file),
          confidence: 0.9,
          timestamp: new Date(),
          software: 'music-metadata'
        });
      }

      if (parsed.common.album) {
        metadata.album = {
          title: parsed.common.album,
          artist: parsed.common.artist,
          track: parsed.common.track?.no,
          totalTracks: parsed.common.track?.of,
          disc: parsed.common.disk?.no,
          totalDiscs: parsed.common.disk?.of,
          genre: parsed.common.genre?.[0],
          year: parsed.common.year,
          bpm: parsed.common.bpm
        };
      }

      if (parsed.common.artist) {
        metadata.creator = parsed.common.artist;
      }

      if (parsed.common.comment) {
        metadata.description = parsed.common.comment;
      }

      if (parsed.common.genre) {
        metadata.album = {
          ...metadata.album,
          genre: parsed.common.genre[0]
        };
      }

      // Cover art
      if (parsed.common.picture && parsed.common.picture.length > 0) {
        const frontCover = parsed.common.picture.find((p: any) => p.type === 'Cover (front)') || parsed.common.picture[0];
        if (frontCover) {
          metadata.artwork = {
            frontCover: `data:${frontCover.format};base64,${frontCover.data.toString('base64')}`
          };
        }
      }

      // Technical metadata
      if (parsed.format) {
        metadata.technical = {
          duration: parsed.format.duration,
          bitrate: parsed.format.bitrate,
          sampleRate: parsed.format.sampleRate,
          channels: parsed.format.numberOfChannels,
          codec: parsed.format.codecName,
          container: parsed.format.container
        };
      }

      // Custom fields from native tags
      const custom: Record<string, any> = {};
      
      // ID3v2 specific tags
      if (parsed.native.id3v2) {
        const id3v2 = parsed.native.id3v2;
        
        // Rating (POPM frame)
        if (id3v2.POPM) {
          const popm = Array.isArray(id3v2.POPM) ? id3v2.POPM[0] : id3v2.POPM;
          if (popm.rating) {
            // Convert 0-255 rating to 0-5 stars
            metadata.rating = Math.round((popm.rating / 255) * 5);
          }
        }

        // Custom ID3v2 frames
        Object.keys(id3v2).forEach(frame => {
          if (!['TIT2', 'TPE1', 'TALB', 'TYER', 'TCON', 'COMM', 'APIC'].includes(frame)) {
            const value = id3v2[frame];
            if (Array.isArray(value)) {
              custom[`id3v2_${frame}`] = value.map(v => v.text || v.data || v);
            } else {
              custom[`id3v2_${frame}`] = value.text || value.data || value;
            }
          }
        });
      }

      // MP4 specific tags
      if (parsed.native.mp4) {
        const mp4 = parsed.native.mp4;
        
        // Custom MP4 atoms
        Object.keys(mp4).forEach(atom => {
          if (!['©nam', '©ART', '©alb', '©day', '©gen', '©cmt', 'covr'].includes(atom)) {
            const value = mp4[atom];
            if (Array.isArray(value)) {
              custom[`mp4_${atom}`] = value.map(v => v.data || v);
            } else {
              custom[`mp4_${atom}`] = value.data || value;
            }
          }
        });
      }

      if (Object.keys(custom).length > 0) {
        metadata.custom = custom;
      }

      // Provenance
      metadata.provenance = {
        created: parsed.common.date ? new Date(parsed.common.date) : undefined,
        software: parsed.common.encodedBy || parsed.common.encodingTool,
        version: parsed.common.encodedBy
      };

      return metadata;
    } catch (error) {
      console.error('music-metadata read error:', error);
      return this.readBasic(file);
    }
  }

  private async readBasic(file: File): Promise<CanonicalMetadata> {
    const metadata: CanonicalMetadata = {
      title: file.name.replace(/\.[^/.]+$/, ""),
      technical: {
        container: file.type.split('/')[1].toUpperCase()
      }
    };

    return metadata;
  }

  async write(file: File, metadata: CanonicalMetadata): Promise<File> {
    // Note: music-metadata is read-only, so we'll need to use a different approach
    // for writing. In a real implementation, you'd use libraries like:
    // - node-id3 for ID3v2 tags
    // - mp4-parser for MP4 atoms
    // - or ExifTool for audio files
    
    if (typeof window !== 'undefined') {
      throw new Error('Audio metadata writing not available in browser environment. Use server-side tools.');
    }
    
    throw new Error('Audio metadata writing not yet implemented. Use ExifTool for audio files.');
  }

  getSupportedExtensions(): string[] {
    return [
      '.mp3', '.flac', '.ogg', '.oga', '.opus', '.m4a', '.aac', '.wma',
      '.wav', '.aiff', '.aif', '.dsf', '.dff', '.ape', '.wv', '.tak'
    ];
  }

  supports(file: File): boolean {
    const extension = '.' + file.name.split('.').pop()?.toLowerCase();
    return this.getSupportedExtensions().includes(extension);
  }

  private getTagSource(file: File): string {
    const extension = '.' + file.name.split('.').pop()?.toLowerCase();
    
    if (['.mp3', '.flac', '.ogg'].includes(extension)) {
      return 'id3';
    } else if (['.m4a', '.aac', '.mp4'].includes(extension)) {
      return 'mp4';
    } else {
      return 'audio';
    }
  }
}

/**
 * ID3v2 Frame Mapping
 * Maps canonical metadata to ID3v2 frames
 */
export const ID3V2_FRAME_MAP = {
  title: 'TIT2',
  artist: 'TPE1',
  album: 'TALB',
  year: 'TYER',
  genre: 'TCON',
  comment: 'COMM',
  track: 'TRCK',
  disc: 'TPOS',
  bpm: 'TBPM',
  key: 'TKEY',
  mood: 'TMOO',
  rating: 'POPM',
  cover: 'APIC'
};

/**
 * MP4 Atom Mapping
 * Maps canonical metadata to MP4 ilst atoms
 */
export const MP4_ATOM_MAP = {
  title: '©nam',
  artist: '©ART',
  album: '©alb',
  year: '©day',
  genre: '©gen',
  comment: '©cmt',
  track: 'trkn',
  disc: 'disk',
  bpm: 'tmpo',
  cover: 'covr'
};
